import _ from 'lodash';
import {createSelector} from 'reselect';

import {AppParametricSelector} from '../../../helpers/reselectHelpers';
import {
  CrmSyncIntegrationResourceTypesEnum,
  CrmSyncModel,
  CrmSyncSourcesEnum,
  mapCrmSyncSourceToIntegrationType,
} from '../../../models/api/crmSyncModel';
import {AppState} from '../../appState';
import {AsyncStatus, ResourceTableStatusesEnum} from '../../appTypes';
import {getFlatIntegrations} from '../dataSelectors';
import {getAllRunningSyncAccountsJobs} from '../jobs/jobsSelectors';

/*
 * Data Selectors.
 */

export function getCrmSyncs(state: AppState) {
  return state.data.crmSyncs.byId;
}

export const getFlatCrmSyncs = createSelector(getCrmSyncs, (crmSyncs) =>
  _(crmSyncs).values().compact().value(),
);

export function getCrmSyncsAsyncStatus(state: AppState): AsyncStatus {
  return state.data.crmSyncs;
}

export const getIsCrmSyncLoading = createSelector(
  getCrmSyncsAsyncStatus,
  (asyncStatus) =>
    asyncStatus.status === ResourceTableStatusesEnum.INITIAL ||
    asyncStatus.status === ResourceTableStatusesEnum.LOADING,
);

/*
 * CRM Sync Selector.
 */

interface CrmSyncProps {
  source: CrmSyncSourcesEnum;
  resourceType: CrmSyncIntegrationResourceTypesEnum;
}

export type CrmSyncSourceSelector<P> = AppParametricSelector<P, CrmSyncSourcesEnum>;
export type CrmSyncResourceTypeSelector<P> = AppParametricSelector<P, CrmSyncIntegrationResourceTypesEnum>;

export function getCrmSyncSource(_state: unknown, props: {source: CrmSyncSourcesEnum}) {
  return props.source;
}

function getCrmSyncResourceType(_state: unknown, props: CrmSyncProps) {
  return props.resourceType;
}

// Via Source and resource type.

export type CrmSyncModelSelectorViaSourceAndResourceType<P> = AppParametricSelector<
  P,
  CrmSyncModel | undefined
>;

export function makeGetCrmSyncViaSourceAndResourceType(): CrmSyncModelSelectorViaSourceAndResourceType<CrmSyncProps>;
export function makeGetCrmSyncViaSourceAndResourceType<P>(
  getSource: CrmSyncSourceSelector<P>,
  getResourceType: CrmSyncResourceTypeSelector<P>,
): CrmSyncModelSelectorViaSourceAndResourceType<P>;
export function makeGetCrmSyncViaSourceAndResourceType(
  getSource: CrmSyncSourceSelector<any> = getCrmSyncSource,
  getResourceType: CrmSyncResourceTypeSelector<any> = getCrmSyncResourceType,
) {
  return createSelector([getFlatCrmSyncs, getSource, getResourceType], (crmSyncs, source, resourceType) =>
    crmSyncs.find((crmSync) => crmSync.source === source && crmSync.resourceType === resourceType),
  );
}

// via Resource Type.

export type CrmSyncModelSelectorViaResourceType<P> = AppParametricSelector<P, ReadonlyArray<CrmSyncModel>>;

export function makeGetCrmSyncViaResourceType(): CrmSyncModelSelectorViaResourceType<
  Omit<CrmSyncProps, 'source'>
>;
export function makeGetCrmSyncViaResourceType<P>(
  getResourceType: CrmSyncResourceTypeSelector<P>,
): CrmSyncModelSelectorViaResourceType<P>;
export function makeGetCrmSyncViaResourceType(
  getResourceType: CrmSyncResourceTypeSelector<any> = getCrmSyncResourceType,
) {
  return createSelector([getFlatCrmSyncs, getResourceType], (crmSyncs, resourceType) =>
    crmSyncs.filter((crmSync) => crmSync.resourceType === resourceType),
  );
}

export function makeGetCrmAccountSyncViaSource(source: CrmSyncSourcesEnum) {
  return createSelector([getFlatCrmSyncs], (crmSyncs) =>
    crmSyncs.find(
      (crmSync) =>
        crmSync.resourceType === CrmSyncIntegrationResourceTypesEnum.ACCOUNT && crmSync.source === source,
    ),
  );
}

export function makeGetAccountCrmSyncViaSource() {
  return createSelector([getFlatCrmSyncs, getCrmSyncSource], (crmSyncs, source) =>
    crmSyncs.find(
      (crmSync) =>
        crmSync.resourceType === CrmSyncIntegrationResourceTypesEnum.ACCOUNT && crmSync.source === source,
    ),
  );
}

export function makeGetCardCrmSyncViaSource() {
  return createSelector([getFlatCrmSyncs, getCrmSyncSource], (crmSyncs, source) =>
    crmSyncs.find(
      (crmSync) =>
        crmSync.resourceType === CrmSyncIntegrationResourceTypesEnum.CARD && crmSync.source === source,
    ),
  );
}

export function makeGetActiveSyncJob(getCrmSync: ReturnType<typeof makeGetCrmAccountSyncViaSource>) {
  return createSelector([getCrmSync, getAllRunningSyncAccountsJobs], (crmSync, jobs) => {
    if (!crmSync || jobs.length === 0) {
      return undefined;
    }
    return jobs.find((job) => job.sync_id === crmSync.id);
  });
}

export function makeGetEnabledIntegrationViaSource(source: CrmSyncSourcesEnum) {
  return createSelector([getFlatIntegrations], (integrations) => {
    const integrationType = mapCrmSyncSourceToIntegrationType(source);
    if (!integrationType) {
      return undefined;
    }

    return integrations.find((i) => i.type === integrationType && i.isEnabled);
  });
}
